import axios from 'axios'
import {useFormik} from 'formik'
import React, {useState} from 'react'
import {Button, Col, Form, Row} from 'react-bootstrap'
import {decodeToken} from 'react-jwt'
import {Link, useNavigate} from 'react-router-dom'
import * as yup from 'yup'
import {toAbsoluteUrl} from '_metronic/helpers'
import FormikErrors from '../../../shared/FormikErrors/FormikErrors'
import {useAuth} from '../core/Auth'
import {getCurrentUserInfo, login, twoFactorLogin} from '../core/_requests'
import TwoFactor, {TFA_EMAIL_PROVIDER, TFA_GOOGLE_AUTHENTICATOR_PROVIDER, TwoFactorProviderOptions} from './TwoFactor'
import Alertize from 'app/shared/Alfacom/Alert/Alertize'
import {VALIDATION, VALIDATION_ERROR} from 'app/shared/Common/Constants'

export const IS_TFA_REQUIRED_CLAIM_TYPE = 'isTwoFactorAuthRequired'
export const IS_PASSWORD_EXPIRED_CLAIM_TYPE = 'isPasswordExpired'
export const PROVIDER_OPTION_CLAIM_TYPE = 'providerOption'

const allAvailableTwoFactorProviderOptions: TwoFactorProviderOptions[] = [
  {
    name: 'Authenticator Apps',
    value: TFA_GOOGLE_AUTHENTICATOR_PROVIDER,
    iconPath: '/media/icons/duotune/coding/cod001.svg',
    description: 'Get code from an app like Google Authenticator, Microsoft Authenticator, Authy or 1Password.',
  },
  {
    name: 'Email',
    value: TFA_EMAIL_PROVIDER,
    iconPath: '/media/icons/duotune/communication/com003.svg',
    description: 'We will send you the code via email.',
  },
]

const loginSchema = yup.object().shape({
  username: yup.string().trim().max(256).required(VALIDATION_ERROR.STRING.REQUIREDx('Username')),
  password: yup
    .string()
    .max(VALIDATION.PASSWORD.LOGIN.MAX, VALIDATION_ERROR.STRING.MAX(VALIDATION.PASSWORD.LOGIN.MAX))
    .required(VALIDATION_ERROR.STRING.REQUIREDx('Password')),
})

const initialValues = {
  username: '',
  password: '',
}

/*
  Formik+YUP+Typescript:
  https://jaredpalmer.com/formik/docs/tutorial#getfieldprops
  https://medium.com/@maurice.de.beijer/yup-validation-and-typescript-and-formik-6c342578a20e
*/

export function Login() {
  const {saveAuth, setCurrentUser} = useAuth()
  const [loading, setLoading] = useState(false)
  const [showTwoFactorAuth, setShowTwoFactorAuth] = useState(false)
  const [twoFactorOptions, setTwoFactorOptions] = useState(allAvailableTwoFactorProviderOptions)
  const navigate = useNavigate()

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      setStatus(undefined)
      try {
        const {data: auth} = await login(values.username, values.password)
        saveAuth(auth)
        // check if two-factor auth is required
        const decodedToken: any = decodeToken(auth.access_token)
        if (decodedToken[IS_PASSWORD_EXPIRED_CLAIM_TYPE] === 'true') {
          navigate('/auth/update-password', {replace: true})
        } else if (decodedToken[IS_TFA_REQUIRED_CLAIM_TYPE] === 'true') {
          // if two-factor auth is required, get available provider options from claims
          const providerOptionClaims = decodedToken[PROVIDER_OPTION_CLAIM_TYPE]
          const providerOptions = Array.isArray(providerOptionClaims) ? [...providerOptionClaims] : [providerOptionClaims]
          // then, show two-factor auth modal
          setTwoFactorOptions(twoFactorOptions.filter((x) => providerOptions.indexOf(x.value) >= 0))
          setShowTwoFactorAuth(true)
        } else {
          const {data: user} = await getCurrentUserInfo()
          setCurrentUser(user)
        }
      } catch (error) {
        console.error(error)
        saveAuth(undefined)
        if (axios.isAxiosError(error) && error.response) {
          setStatus(error.response.data.error_description)
        } else {
          setStatus(`Login details are incorrect`)
        }
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  const handleTwoFactorAuthClose = () => {
    setShowTwoFactorAuth(false)
    setLoading(false)
    formik.setSubmitting(false)
  }

  const handleTwoFactorAuthError = (error: any) => {
    setShowTwoFactorAuth(false)
    setLoading(false)
    formik.setSubmitting(false)
    if (error && error.response) {
      formik.setStatus(error.response.data.Message)
    } else {
      formik.setStatus('Failed to send two-factor authentication code.')
    }
  }

  const handleTwoFactorAuthSubmit = async (code: string, provider: string) => {
    setShowTwoFactorAuth(false)
    setLoading(true)
    formik.setSubmitting(true)
    formik.setStatus(undefined)
    try {
      const {data: auth} = await twoFactorLogin(formik.values.username, formik.values.password, code, provider)
      saveAuth(auth)
      const {data: user} = await getCurrentUserInfo()
      setCurrentUser(user)
    } catch (error) {
      console.error(error)
      if (axios.isAxiosError(error) && error.response) {
        formik.setStatus(error.response.data.error_description)
      } else {
        formik.setStatus(`Failed to login with two-factor authentication.`)
      }
      formik.setSubmitting(false)
      setLoading(false)
    }
  }

  return (
    <div className='w-100 mw-450px h-100 d-flex flex-center'>
      <Row className='g-10'>
        <Col xs={12}>
          <div className='d-flex flex-column flex-lg-row align-items-center justify-content-around gap-4'>
            <a href='#' className=''>
              <img alt='Logo' src={toAbsoluteUrl('/media/logos/logo_reversed.svg')} className='h-75px h-lg-100px' />
            </a>
            <h1 className='fs-2x fw-bolder w-lg-300px text-center text-lg-start'>Information Management Portal</h1>
          </div>
        </Col>
        <Col xs={12} className='text-center'>
          <div className='separator separator-content mt-4 mb-6'>
            <h2 className='text-gray-900 fs-1 text-nowrap'>Sign In</h2>
          </div>
        </Col>
        <Col xs={12}>
          <Form>
            {formik.status != undefined && <Alertize variant='danger' faIcon={'fas fa-triangle-exclamation'} description={<>{formik.status}</>} />}

            <Form.Group>
              {/* <Form.Label className={`fw-bold fs-6`}>Username</Form.Label> */}
              <Col className='d-flex flex-column gap-2'>
                <Form.Control
                  {...formik.getFieldProps('username')}
                  type='text'
                  placeholder='Username'
                  value={formik.values.username || ''}
                  maxLength={256}
                  disabled={formik.isSubmitting}
                  autoComplete='username'
                  autoFocus={true}
                />

                <FormikErrors formik={formik} fieldKey={'username'} />
              </Col>
            </Form.Group>

            <Form.Group>
              {/* <Form.Label className={`fw-bold fs-6 d-flex`}>Password</Form.Label> */}
              <Col className='d-flex flex-column gap-2'>
                <Form.Control
                  {...formik.getFieldProps('password')}
                  type='password'
                  placeholder='Password'
                  value={formik.values.password || ''}
                  maxLength={50}
                  disabled={formik.isSubmitting}
                  autoComplete='current-password'
                />

                <FormikErrors formik={formik} fieldKey={'password'} />
              </Col>
            </Form.Group>
            <Form.Group className='text-end'>
              <Link to={formik.isSubmitting ? '' : '/auth/forgot-password'} className='fs-6'>
                Forgot Password ?
              </Link>
            </Form.Group>

            <Form.Group className='mt-10'>
              <div className='d-flex'>
                <Button
                  type='submit'
                  className='flex-grow-1'
                  onClick={(e) => {
                    e.preventDefault()
                    formik.handleSubmit()
                  }}
                  disabled={formik.isSubmitting}
                >
                  {!loading && <span className='indicator-label'>Sign In</span>}
                  {loading && (
                    <span className='indicator-progress' style={{display: 'block'}}>
                      <span className='spinner-border spinner-border-sm align-middle me-2'></span>
                      Please wait...
                    </span>
                  )}
                </Button>
              </div>
            </Form.Group>
          </Form>
          <TwoFactor
            show={showTwoFactorAuth}
            options={twoFactorOptions}
            onClose={handleTwoFactorAuthClose}
            onSubmit={handleTwoFactorAuthSubmit}
            onError={handleTwoFactorAuthError}
          />
        </Col>
      </Row>
    </div>
  )
}
